// apps/web – Provider für Clubmeisterschaft-Setup (Hooks + Persistence aus services)

import React, { useMemo } from "react";
import { __asStr } from "@tennis-matches/core/utils";
import { ClubmeisterschaftSetupContext } from "@tennis-matches/clubmeisterschaft-core/ui";
import { useTournamentSetup } from "@tennis-matches/services/tournament/hooks";
import {
  useSetupWizard,
  useWizardValidation,
} from "@tennis-matches/services/tournament-wizard";
import {
  saveTournamentSetupRemote,
  upsertTournamentBootstrapWithAuditRemote,
} from "@tennis-matches/services/persistence";
import {
  createClubPlayerContactFromParsedName,
  prepareClubPlayerContactFromName,
  searchClubPlayerContacts,
  addPlayer,
  deletePlayer,
} from "@tennis-matches/services/players";

const WIZARD_VALIDATION_CONFIG = {
  players: {
    minPlayers: { singles: 8, fixedDoubles: 8, default: 8 },
    warnRange: { min: 8, max: 24 },
  },
  features: { expertMode: true, skipMetaDateTime: true },
  modes: {
    simple: {
      steps: [
        "meta",
        "players",
        "mode",
        "details",
        "periods",
        "rules_cm",
        "review",
      ],
    },
    expert: {
      steps: [
        "meta",
        "players",
        "mode",
        "details",
        "periods",
        "rules_cm",
        "review",
      ],
    },
  },
};

function getWantNewSetupFromUrl() {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const v = __asStr(sp.get("new") || "").toLowerCase();
    return v === "1" || v === "true" || v === "yes";
  } catch (_) {
    return false;
  }
}

/**
 * Füllt ClubmeisterschaftSetupContext mit Hooks und Persistence aus services.
 * Wird bei route clubmeisterschaft-setup um ClubmeisterschaftRoot gelegt (z. B. in DynamicAppLoader).
 */
export function ClubmeisterschaftSetupProvider({ state, t, children }) {
  const wantNewSetupFromUrl = useMemo(() => getWantNewSetupFromUrl(), []);

  const setupState = useTournamentSetup(state, "club", wantNewSetupFromUrl);
  const {
    tournamentName,
    startDate,
    startTime,
    format,
    names,
    minutesPerRound,
    totalMinutes,
    courtsCount,
    courtsNumbersRaw,
    pointsWin,
    pointsDraw,
    wizardMode,
    teamAllocationMode,
    manualTeams,
    lkByPlayer,
    excludesByPlayer,
    rrMode,
    playoffVorrunden,
    playoffKoTeilnehmer,
    playThirdPlace,
    tournamentMode,
    groupsKo,
    periods,
    setPeriods,
    matchRules,
    koOnly,
  } = setupState;

  const actualPlayerCount = useMemo(() => {
    if (!names || typeof names !== "object") return 0;
    return Object.values(names).filter((name) => __asStr(name).length > 0)
      .length;
  }, [names]);

  const setup = useMemo(
    () => ({
      tournamentName,
      startDate,
      startTime,
      format,
      tournamentMode: tournamentMode || "groupsKo",
      playerCount: actualPlayerCount,
      names: names || {},
      minutesPerRound,
      totalMinutes,
      courtCount: courtsCount,
      courtNumbers: courtsNumbersRaw,
      winPoints: pointsWin,
      drawPoints: pointsDraw,
      wizardMode: wizardMode || "simple",
      groupsKo: groupsKo || {},
      koOnly: koOnly || {},
      periods: periods || {},
      matchRules: matchRules || { scoring: "bo3", setEnd: "tb" },
    }),
    [
      tournamentName,
      startDate,
      startTime,
      format,
      tournamentMode,
      actualPlayerCount,
      names,
      minutesPerRound,
      totalMinutes,
      courtsCount,
      courtsNumbersRaw,
      pointsWin,
      pointsDraw,
      wizardMode,
      groupsKo,
      koOnly,
      periods,
      matchRules,
    ],
  );

  const stepIds = useMemo(
    () => [
      "meta",
      "players",
      "mode",
      "details",
      "periods",
      "rules_cm",
      "review",
    ],
    [],
  );

  const { validation, stepOk } = useWizardValidation(
    setup,
    WIZARD_VALIDATION_CONFIG,
    t,
  );
  const wizard = useSetupWizard(
    wizardMode,
    stepOk,
    "ttb:club:setupMode",
    stepIds,
  );

  const onSave = useMemo(
    () =>
      async ({ tournamentData, schedule, playersArr }) => {
        const dbResult = await saveTournamentSetupRemote({
          tournament: tournamentData,
          schedule,
          players: playersArr,
        });
        if (!dbResult?.ok) {
          return {
            ok: false,
            reason: dbResult?.reason,
            error: dbResult?.error,
            tournamentId: dbResult?.tournamentId,
          };
        }
        await upsertTournamentBootstrapWithAuditRemote({
          code: tournamentData?.meta?.code,
          kind: "clubmeisterschaft",
          source: "setup-react",
          payload: {
            v: 1,
            at: Date.now(),
            tournament: tournamentData,
            schedule,
          },
        }).catch(() => {});
        return { ok: true, tournamentId: dbResult.tournamentId };
      },
    [],
  );

  const onBootstrapNew = useMemo(
    () => (code) => {
      return upsertTournamentBootstrapWithAuditRemote({
        code,
        kind: "clubmeisterschaft",
        meta: { code },
        payload: null,
        source: "setup-new",
      });
    },
    [],
  );

  const onSearchPlayers = useMemo(
    () =>
      (query, limit = 10) =>
        searchClubPlayerContacts(query, limit),
    [],
  );
  const onAddPlayer = useMemo(() => (name) => addPlayer(name), []);
  const onPreparePlayerContact = useMemo(
    () => (name) => prepareClubPlayerContactFromName(name),
    [],
  );
  const onCreatePlayerContact = useMemo(
    () => (parsedName) => createClubPlayerContactFromParsedName(parsedName),
    [],
  );
  const onDeletePlayer = useMemo(
    () => (playerId) => deletePlayer(playerId),
    [],
  );
  const buildRegistrationUrl = useMemo(
    () =>
      ({ code, tournament }) => {
        const c = __asStr(code);
        if (!c) return "";
        try {
          const baseUrl =
            typeof window !== "undefined" ? window.location.origin : "";
          const base = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
          return `${base || ""}#share?clubRegistration=${encodeURIComponent(c)}`;
        } catch (_) {
          return "";
        }
      },
    [],
  );
  const onPersistRegistrationBootstrap = useMemo(
    () =>
      ({ code, tournament }) => {
        const c = __asStr(code);
        if (!c) return Promise.resolve({ ok: false, reason: "missing-code" });
        return upsertTournamentBootstrapWithAuditRemote({
          code: c,
          kind: "clubmeisterschaft",
          source: "registration-link",
          payload: { tournament: tournament || {}, schedule: [] },
        });
      },
    [],
  );

  const contextValue = useMemo(
    () => ({
      t,
      setupState,
      validation: { validation, stepOk },
      wizard,
      onSave,
      onBootstrapNew,
      onSearchPlayers,
      onAddPlayer,
      onPreparePlayerContact,
      onCreatePlayerContact,
      onDeletePlayer,
      buildRegistrationUrl,
      onPersistRegistrationBootstrap,
    }),
    [
      t,
      setupState,
      validation,
      stepOk,
      wizard,
      onSave,
      onBootstrapNew,
      onSearchPlayers,
      onAddPlayer,
      onPreparePlayerContact,
      onCreatePlayerContact,
      onDeletePlayer,
      buildRegistrationUrl,
      onPersistRegistrationBootstrap,
    ],
  );

  return (
    <ClubmeisterschaftSetupContext.Provider value={contextValue}>
      {children}
    </ClubmeisterschaftSetupContext.Provider>
  );
}
